import { Injectable } from '@angular/core';
import Pusher from 'pusher-js';

import { environment } from '@env/environment';
import { ModeService } from '../config/mode.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export enum PusherChannel {
  ORDERS = 'orders',
}

export enum PusherEvent {
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  ORDER_SUBMITTED = 'order-submitted',
  ORDER_STATUS_UPDATE = 'order-status-update',
  ORDER_ITEM_UPDATE = 'order-item-update',
  ORDER_UPDATED = 'order-updated',
}

@Injectable({
  providedIn: 'root',
})
export class PusherService {
  private client: Pusher;

  private pusherListener = new Subject<{ event: string; orderId?: string }>();

  private connectionState = new BehaviorSubject<boolean>(false);

  private channels: Record<string, any> = {};

  get isConnected() {
    return this.client.connection.state === 'connected';
  }

  constructor(private modeService: ModeService) {
    const mode = this.modeService.getMode();
    const pusherConfig = environment[mode].pusher;
    this.client = new Pusher(pusherConfig.key, {
      cluster: pusherConfig.cluster,
    });
    this.client.connection.bind('error', (error: any) => {
      console.error('Pusher connection error', error);
    });
    this.client.connection.bind('state_change', (states: { previous: string; current: string }) => {
      this.publishConnectionState(states.current);
    });
    this.publishConnectionState(this.client.connection.state);
  }

  private publishConnectionState(state: string) {
    const connected = state === 'connected';
    this.connectionState.next(connected);
    this.pusherListener.next({
      event: connected ? PusherEvent.CONNECTED : PusherEvent.DISCONNECTED,
    });
  }

  private subscribe(channel: string, event: string, callback: (data: any) => void) {
    this.channels[`${channel}-${event}`] = this.client.subscribe(channel).bind(event, callback);
  }

  public getPusherListener() {
    return this.pusherListener.asObservable();
  }

  public getConnectionState$(): Observable<boolean> {
    return this.connectionState.asObservable();
  }

  public ensureConnected() {
    const { state } = this.client.connection;
    if (state !== 'connected' && state !== 'connecting') {
      this.client.connect();
    }
  }

  public setupPusher(channel: PusherChannel, venueId: string, event: PusherEvent) {
    this.ensureConnected();
    const channelName = `${channel}-${venueId}`;
    // subscribe() stores `${channelName}-${event}`. A shorter key never hits this guard.
    const channelKey = `${channelName}-${event}`;
    if (this.channels[channelKey]) {
      return;
    }
    this.subscribe(channelName, event, (data: { orderId: string }) => {
      this.pusherListener.next({ event, orderId: data.orderId });
    });
  }

  public unsubscribe(channel: PusherChannel, event: PusherEvent) {
    this.client.unsubscribe(channel);
    delete this.channels[`${channel}-${event}`];
  }

  public reconnect() {
    if (this.client.connection.state !== 'connected') {
      this.client.connect();
      return;
    }
    this.publishConnectionState(this.client.connection.state);
  }

  public disconnect() {
    this.client.disconnect();
  }
}
